import { useState } from 'react'
import type { SignalState, Position } from '../lib/supabase'

type SleeveFilter = 'all' | 'etf' | 'stock' | 'ipo' | 'commod'

interface Props {
  signals: SignalState[]
  sleeveMap?: Record<string, string>
  positions?: Position[]
}

const SIGNAL_COMPONENTS = [
  { key: 'rs_check',      label: 'RS',   description: 'Relative Strength vs RSP' },
  { key: 'vwap_qual',     label: 'VWAP', description: 'Above VWAP from Low' },
  { key: 'pct_chg_check', label: 'PCT',  description: 'Not Overextended (<25%)' },
  { key: 'mom_check',     label: 'MOM',  description: 'Positive Momentum' },
  { key: 'price_check',   label: 'PRC',  description: 'Price $5-$250' },
  { key: 'vol_check',     label: 'VOL',  description: 'Volume >750K' },
  { key: 'rsp_over_vwap', label: 'MKT',  description: 'Market Health (RSP>VWAP)' },
] as const

const SLEEVE_LABELS: Record<SleeveFilter, string> = {
  all: 'ALL', etf: 'ETF', stock: 'STOCK', ipo: 'IPO', commod: 'COMMOD',
}

function signalItemStyle(signal: SignalState, hasPosition: boolean) {
  if (signal.entry_signal) return {
    borderColor: 'var(--accent-cyan-muted)',
    background: 'var(--accent-cyan-glow)',
    tickerColor: 'var(--accent-cyan)',
  }
  if (hasPosition && signal.trend_qual) return {
    borderColor: '#f59e0b80',
    background: 'rgba(245,158,11,0.10)',
    tickerColor: '#f59e0b',
  }
  if (hasPosition) return {
    borderColor: '#f9731660',
    background: 'rgba(249,115,22,0.07)',
    tickerColor: '#f97316',
  }
  if (signal.trend_qual) return {
    borderColor: 'var(--positive-muted)',
    background: 'var(--positive-bg)',
    tickerColor: 'var(--positive)',
  }
  return {
    borderColor: 'var(--border-subtle)',
    background: 'var(--bg-tertiary)',
    tickerColor: 'var(--text-primary)',
  }
}

export function SignalStatus({ signals, sleeveMap = {}, positions = [] }: Props) {
  const [filterSleeve, setFilterSleeve] = useState<SleeveFilter>('all')

  const latestSignals = signals.reduce((acc, signal) => {
    if (!acc[signal.ticker] || signal.date > acc[signal.ticker].date) {
      acc[signal.ticker] = signal
    }
    return acc
  }, {} as Record<string, SignalState>)

  const allSignals = Object.values(latestSignals).sort((a, b) => {
    if (a.trend_qual !== b.trend_qual) return a.trend_qual ? -1 : 1
    return a.ticker.localeCompare(b.ticker)
  })

  const signalList = filterSleeve === 'all'
    ? allSignals
    : allSignals.filter(s => (sleeveMap[s.ticker] || '').toLowerCase() === filterSleeve)

  // Commod tickers in the universe with no signal rows (watchlist-only, e.g. GLD/SLV/GDX/GDXJ)
  const commódWatchlistOnly = Object.entries(sleeveMap)
    .filter(([ticker, sleeve]) => sleeve.toLowerCase() === 'commod' && !latestSignals[ticker])
    .map(([ticker]) => ticker)
    .sort()

  const qualifiedCount = allSignals.filter(s => s.trend_qual).length
  const entrySignalCount = allSignals.filter(s => s.entry_signal).length

  const TRACKED_SLEEVES = ['etf', 'stock', 'ipo', 'commod'] as const

  const sleeveCounts = TRACKED_SLEEVES.reduce((acc, sl) => {
    acc[sl] = Object.values(sleeveMap).filter(s => s.toLowerCase() === sl).length
    return acc
  }, {} as Record<string, number>)

  const deployedCounts = TRACKED_SLEEVES.reduce((acc, sl) => {
    acc[sl] = positions.filter(p => p.sleeve.toLowerCase() === sl && p.quantity > 0).length
    return acc
  }, {} as Record<string, number>)

  const activePositionTickers = new Set(
    positions.filter(p => p.quantity > 0).map(p => p.ticker)
  )

  const positionByTicker = positions.reduce((acc, p) => {
    if (p.quantity > 0) acc[p.ticker] = p
    return acc
  }, {} as Record<string, Position>)

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">TrendQual Signal Status</h2>
        <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="card-badge" style={{ background: 'var(--positive-bg)', color: 'var(--positive)' }}>
            {qualifiedCount} Qualified
          </span>
          {entrySignalCount > 0 && (
            <span className="card-badge" style={{ background: 'var(--accent-cyan-glow)', color: 'var(--accent-cyan)' }}>
              {entrySignalCount} Entry Signals
            </span>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
            {(['all', 'etf', 'stock', 'ipo', 'commod'] as SleeveFilter[]).map(sl => (
              <button
                key={sl}
                className="card-badge"
                onClick={() => setFilterSleeve(sl)}
                style={{
                  cursor: 'pointer',
                  border: 'none',
                  background: filterSleeve === sl
                    ? sl === 'all' ? 'var(--accent-cyan-glow)' : sl === 'commod' ? 'rgba(132,204,22,0.15)' : `var(--sleeve-${sl})20`
                    : 'var(--bg-tertiary)',
                  color: filterSleeve === sl
                    ? sl === 'all' ? 'var(--accent-cyan)' : sl === 'commod' ? 'var(--sleeve-commod)' : `var(--sleeve-${sl})`
                    : 'var(--text-muted)',
                }}
              >
                {sl === 'all'
                  ? `ALL (${allSignals.length})`
                  : `${SLEEVE_LABELS[sl]} (${deployedCounts[sl] ?? 0}/${sleeveCounts[sl] ?? 0})`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 'var(--space-lg)', marginBottom: 'var(--space-md)', flexWrap: 'wrap', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
        {SIGNAL_COMPONENTS.map(comp => (
          <div key={comp.key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{comp.label}</span>
            <span>= {comp.description}</span>
          </div>
        ))}
      </div>

      {/* Color key */}
      <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)', flexWrap: 'wrap', fontSize: '0.65rem' }}>
        <span style={{ color: 'var(--accent-cyan)' }}>■ Entry signal</span>
        <span style={{ color: '#f59e0b' }}>■ In position · qualified</span>
        <span style={{ color: 'var(--positive)' }}>■ Qualified · no position</span>
        <span style={{ color: '#f97316' }}>■ In position · not qualified</span>
        <span style={{ color: 'var(--text-muted)' }}>■ Watching</span>
      </div>

      {signalList.length === 0 && commódWatchlistOnly.length === 0 && filterSleeve !== 'commod' ? (
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <div className="empty-state-text">No signal data available</div>
        </div>
      ) : (
        <>
          {/* Signal cards — all sleeves (including commod tickers that have signal rows) */}
          {signalList.length > 0 && (
            <div className="signal-grid">
              {signalList.map((signal) => {
                const passCount = SIGNAL_COMPONENTS.filter(
                  comp => signal[comp.key as keyof SignalState] === true
                ).length
                const hasPosition = activePositionTickers.has(signal.ticker)
                const { borderColor, background, tickerColor } = signalItemStyle(signal, hasPosition)

                return (
                  <div key={signal.ticker} className="signal-item" style={{ borderColor, background }}>
                    <span className="signal-ticker" style={{ color: tickerColor }}>
                      {signal.ticker}
                      {hasPosition && <span style={{ fontSize: '0.55rem', marginLeft: 3, opacity: 0.8 }}>●</span>}
                    </span>

                    <div className="signal-status" title={`${passCount}/7 filters passing`}>
                      {SIGNAL_COMPONENTS.map(comp => (
                        <div
                          key={comp.key}
                          className={`signal-dot ${signal[comp.key as keyof SignalState] ? 'pass' : 'fail'}`}
                          title={`${comp.label}: ${signal[comp.key as keyof SignalState] ? 'PASS' : 'FAIL'}`}
                        />
                      ))}
                    </div>

                    {signal.entry_signal ? (
                      <span className="signal-label" style={{ color: 'var(--accent-cyan)' }}>{signal.entry_signal}</span>
                    ) : hasPosition && signal.trend_qual ? (
                      <span className="signal-label" style={{ color: '#f59e0b' }}>IN · QUAL</span>
                    ) : hasPosition ? (
                      <span className="signal-label" style={{ color: '#f97316' }}>IN · WATCH</span>
                    ) : signal.trend_qual ? (
                      <span className="signal-label" style={{ color: 'var(--positive)' }}>QUALIFIED</span>
                    ) : (
                      <span className="signal-label">{passCount}/7</span>
                    )}

                    {signal.rs_diff !== null && (
                      <div style={{ fontSize: '0.6rem', fontFamily: 'var(--font-mono)', color: signal.rs_diff > 0 ? 'var(--positive)' : 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
                        RS: {signal.rs_diff > 0 ? '+' : ''}{signal.rs_diff.toFixed(1)}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Commod watchlist-only tickers (no signal rows — GLD/SLV/GDX/GDXJ) */}
          {filterSleeve === 'commod' && commódWatchlistOnly.length > 0 && (
            <>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: 'var(--space-md) 0 var(--space-sm)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Watchlist — no active signal
              </div>
              <div className="signal-grid">
                {commódWatchlistOnly.map(ticker => {
                  const pos = positionByTicker[ticker]
                  return (
                    <div
                      key={ticker}
                      className="signal-item"
                      style={{
                        borderColor: pos ? '#f9731660' : 'var(--border-subtle)',
                        background: pos ? 'rgba(249,115,22,0.07)' : 'var(--bg-tertiary)',
                      }}
                    >
                      <span className="signal-ticker" style={{ color: pos ? '#f97316' : 'var(--text-muted)' }}>
                        {ticker}
                        {pos && <span style={{ fontSize: '0.55rem', marginLeft: 3, opacity: 0.8 }}>●</span>}
                      </span>
                      {pos ? (
                        <>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                            {pos.quantity} sh · ${pos.current_price.toFixed(2)}
                          </div>
                          <span className="signal-label" style={{ color: '#f97316' }}>IN · NO SIGNAL</span>
                        </>
                      ) : (
                        <span className="signal-label">WATCHING</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
